import clsx from 'clsx';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import PasswordChecklist from 'react-password-checklist';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { resetPassword } from '../core/_requests';

const initialValues = {
  password: '',
  confirmPassword: '',
};

export function ResetPassword() {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [confirmPasswordCheck, setConfirmPasswordCheck] = useState<string>('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const formik = useFormik({
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      if (!token) {
        SystemNotification.error('Token de redefinição de senha inválido');
        return;
      }

      if (values.password !== confirmPasswordCheck) {
        SystemNotification.error('A nova senha e a confirmação não conferem');
        return;
      }

      setLoading(true);
      try {
        await resetPassword(token, values.password);
        SystemNotification.success('Senha redefinida com sucesso!');
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      } catch (error) {
        SystemNotification.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <form noValidate className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework" onSubmit={formik.handleSubmit}>
      <div className="text-center mb-10">
        <h1 className="text-gray-900 fw-bolder mb-3">Defina a sua nova senha</h1>
        <div className="text-gray-500 fw-semibold fs-6">Certifique-se de memorizar a sua nova senha</div>
      </div>

      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-gray-900 fs-6">Nova senha</label>
        <input
          autoComplete="off"
          placeholder="Digite sua nova senha"
          type="password"
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.password && formik.errors.password },
            { 'is-valid': formik.touched.password && !formik.errors.password },
          )}
        />
        {!!formik.touched.password && !!formik.errors.password && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className="fv-row mb-8">
        <label className="form-label fw-bolder text-gray-900 fs-6">Repita sua nova senha</label>
        <input
          autoComplete="off"
          className="form-control bg-transparent"
          placeholder="Confirme sua nova senha"
          type="password"
          value={confirmPasswordCheck}
          onChange={(value) => setConfirmPasswordCheck(value.target.value)}
        />
      </div>

      <div className="d-flex flex-column justify-content-center align-items-center my-4">
        <span className="fw-bold text-gray-600 mb-3 fs-6">Sua senha deve conter:</span>
        <PasswordChecklist
          iconSize={12}
          invalidColor="#D12446"
          messages={{
            minLength: 'Mínimo 6 caracteres',
            letter: 'Pelo menos uma letra',
            number: 'Pelo menos um número',
            match: 'As senhas não coincidem',
          }}
          minLength={6}
          rules={['minLength', 'letter', 'number', 'match']}
          validColor="#4BCA81"
          value={formik.values.password}
          valueAgain={confirmPasswordCheck}
          onChange={(isValid) => setIsPasswordValid(isValid)}
        />
      </div>

      <div className="d-flex flex-wrap justify-content-center pb-lg-0">
        <button className="btn btn-primary" disabled={loading || !token || !isPasswordValid} type="submit">
          <span className="indicator-label" style={{ display: loading ? 'none' : 'inline' }}>
            Salvar nova senha
          </span>
          <span className="indicator-progress" style={{ display: loading ? 'inline' : 'none' }}>
            Aguarde...
            <span className="spinner-border spinner-border-sm align-middle ms-2" />
          </span>
        </button>
      </div>
    </form>
  );
}
